import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper } from 'hb-builder/components/common/Wrapper/WrapperRender';
import { useRendererContext, useSectionContext } from 'hb-builder/hooks';
import type { RenderComponent } from 'hb-builder/types';
import { getAlignItems } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ColumnItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/columnItemAttributes';

const interpolateWidth = ({ width = '100%' }: { width: string | number }) =>
  typeof width === 'string' ? width : `${width}%`;

const componentType = 'column';

const Column: RenderComponent = () => <div />; // never used

const Render = ({
  children,
  item = {
    id: '',
    type: '',
  },
}: RenderProps<DocumentItem<ColumnItemAttributes>>) => {
  const { isMobilePreview, scaleRatio } = useRendererContext();
  const { attributes: sectionAttributes, sectionHeight } = useSectionContext();
  const isMobileDevice = useIsMobileDevice();
  const breakpoint = useBreakpoint();

  const { attributes, children: childrenItem } = item;

  // TODO: very complex logic. Should be removed when section min-height is deprecated.
  const isMobile =
    isMobilePreview || isMobileDevice || ['s', 'm'].includes(breakpoint);
  const areColumnSideBySide = sectionAttributes?.mobileDisplay === 'group';
  const shouldApplySectionHeight =
    (!isMobile || (isMobile && areColumnSideBySide)) &&
    scaleRatio === 1 &&
    sectionHeight;
  const hasChildren = childrenItem?.length && childrenItem?.length > 0;
  const columnHeight =
    shouldApplySectionHeight && !hasChildren ? `${sectionHeight}px` : 'auto';

  return (
    // @ts-expect-error Wrapper is not typed
    <Wrapper
      attributes={attributes}
      // The parent section uses this classname to override the column width on mobile
      className="builder-column"
      componentType={componentType}
      isMobilePreview={isMobilePreview}
      style={{
        alignSelf: 'stretch',
        justifyContent: getAlignItems(attributes?.verticalAlign),
        flexDirection: 'column',
        height: columnHeight,
        width: attributes?.width
          ? interpolateWidth({
              width: attributes.width,
            })
          : null,
      }}
    >
      {children}
    </Wrapper>
  );
};

Column.componentName = 'Column';
Column.Render = Render;

Column.item = ({
  disableSelection = false,
  verticalAlign = 'top',
  height = '100%',
  width = '100%',
  children = [],
} = {}) => ({
  attributes: { disableSelection, verticalAlign, height, width },
  type: Column.componentName,
  children,
});

export default Column;
